import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ElementContainer from 'components/shared/ElementContainer';
import MosaicDistanceViewer from './MosaicDistanceViewer';

const FALLBACK_PRESETS = {
  balanced: {
    max_dimension: 700,
    feature_match_ratio: 0.70,
    min_good_matches: 7,
    homography_method: 'magsac',
    reprojection_threshold_px: 3.0,
    blend_method: 'distance_transform',
    neighbor_radius: 3,
    nonlocal_candidates_per_frame: 8,
    require_spatial_distribution: true,
  },
  strict_clean: {
    max_dimension: 700,
    feature_match_ratio: 0.68,
    min_good_matches: 8,
    homography_method: 'magsac',
    reprojection_threshold_px: 2.5,
    blend_method: 'distance_transform',
    neighbor_radius: 3,
    nonlocal_candidates_per_frame: 6,
    require_spatial_distribution: true,
  },
  more_coverage: {
    max_dimension: 700,
    feature_match_ratio: 0.75,
    min_good_matches: 6,
    homography_method: 'magsac',
    reprojection_threshold_px: 3.5,
    blend_method: 'distance_transform',
    neighbor_radius: 4,
    nonlocal_candidates_per_frame: 10,
    require_spatial_distribution: true,
  },
};

const FALLBACK_SETTING_FIELDS = {
  max_dimension: { minimum: 200, maximum: 2000, step: 50, label: 'Maximum processing resolution', unit: 'px', help: 'Limits the longest side of each image while stitching. Aspect ratio is preserved.' },
  feature_match_ratio: { minimum: 0.45, maximum: 0.95, step: 0.01, label: 'Match strictness', help: 'Lower values require closer feature agreement; higher values allow more possible matches.' },
  min_good_matches: { minimum: 4, maximum: 100, step: 1, label: 'Minimum verified matching points', help: 'Minimum number of geometrically consistent feature points needed before two images can connect.' },
  reprojection_threshold_px: { minimum: 0.5, maximum: 12, step: 0.1, label: 'Match position tolerance', unit: 'px', help: 'Maximum pixel error allowed when checking whether matched points agree geometrically.' },
  neighbor_radius: { minimum: 1, maximum: 8, step: 1, label: 'Nearby images to check', unit: 'images', help: 'Checks this many images forward in capture order. Visually similar distant images are checked separately.' },
  nonlocal_candidates_per_frame: { minimum: 0, maximum: 20, step: 1, label: 'Extra distant images to check', unit: 'images', help: 'Also checks this many visually similar images outside the nearby capture-order range.' },
};

const CAMERA_CALIBRATION_DEFAULTS = {
  camera_calibration_enabled: false,
  camera_model: 'standard',
  camera_fx: null,
  camera_fy: null,
  camera_cx: null,
  camera_cy: null,
  camera_k1: null,
  camera_k2: null,
  camera_p1: null,
  camera_p2: null,
  camera_k3: null,
  camera_k4: null,
  camera_k5: null,
  camera_k6: null,
  camera_calibration_width: null,
  camera_calibration_height: null,
  camera_balance: 0,
};

const PRESET_LABELS = {
  balanced: 'Balanced',
  strict_clean: 'Strict / Clean',
  more_coverage: 'More Coverage',
  custom: 'Custom',
};

const SETTING_LABELS = {
  preset: 'Preset',
  max_dimension: 'Maximum processing resolution',
  feature_match_ratio: 'Match strictness',
  min_good_matches: 'Minimum verified matching points',
  reprojection_threshold_px: 'Match position tolerance',
  blend_method: 'Image blending',
  neighbor_radius: 'Nearby images to check',
  nonlocal_candidates_per_frame: 'Extra distant images to check',
  require_spatial_distribution: 'Reject clustered matching points',
  camera_calibration_enabled: 'Correct camera lens distortion',
  camera_fx: 'Camera fx',
  camera_fy: 'Camera fy',
  camera_cx: 'Camera cx',
  camera_cy: 'Camera cy',
  camera_k1: 'Distortion k1',
  camera_k2: 'Distortion k2',
  camera_p1: 'Tangential p1',
  camera_p2: 'Tangential p2',
  camera_k3: 'Distortion k3',
  camera_k4: 'Distortion k4',
  camera_k5: 'Distortion k5',
  camera_k6: 'Distortion k6',
  camera_calibration_width: 'Calibration image width',
  camera_calibration_height: 'Calibration image height',
  camera_balance: 'Lens correction balance',
};

// Compare numbered filenames naturally, so 2.png sorts before 10.png.
// Keep originalIndex unchanged: API image_order, exclusions and overlap hints
// refer to the original upload positions, not these display positions.
const imageFilenameComparator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});
const sortImagesByFilename = (images) => [...images].sort(
  (a, b) => imageFilenameComparator.compare(a.name, b.name) ||
    a.originalIndex - b.originalIndex
);

const ImageMosaicArtifact = (props) => {
  const {
    id,
    user,
    token,
    title,
    titleColor,
    prompt,
    requirement,
    requirable,
    reviewable,
    maxImages,
    maxUploadSizeBytes,
    maxUploadSizeMb,
  } = props;

  const endpoint = useMemo(
    () => axios.create({ headers: { authorization: `bearer ${token}` } }),
    [token]
  );

  const [media, setMedia] = useState(props.media || null);
  const [mosaic, setMosaic] = useState(props.image_mosaic || null);
  const [status, setStatus] = useState(props.status);
  const [completed, setCompleted] = useState(props.completed);
  const [items, setItems] = useState([]);
  const [excluded, setExcluded] = useState(new Set(props.image_mosaic?.excluded_images || []));
  const [hints, setHints] = useState(props.image_mosaic?.adjacency_hints || []);
  const [hintFrom, setHintFrom] = useState('');
  const [hintTo, setHintTo] = useState('');
  const [settingsMeta, setSettingsMeta] = useState({ presets: FALLBACK_PRESETS });
  const [settings, setSettings] = useState({
    preset: 'balanced',
    ...FALLBACK_PRESETS.balanced,
    ...CAMERA_CALIBRATION_DEFAULTS,
  });
  const [advanced, setAdvanced] = useState(false);
  const [activeHelpTip, setActiveHelpTip] = useState(null);
  const [helpTipPosition, setHelpTipPosition] = useState(null);
  const [calibrationFileName, setCalibrationFileName] = useState('');
  const [calibrationReprojectionError, setCalibrationReprojectionError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ completed: 0, total: 0 });
  const [showReplaceWarning, setShowReplaceWarning] = useState(false);
  const [largeResultUrl, setLargeResultUrl] = useState(null);
  const [showCompareViewer, setShowCompareViewer] = useState(false);
  const [showDistanceViewer, setShowDistanceViewer] = useState(false);
  const [showProcessingLog, setShowProcessingLog] = useState(false);
  const [expandProcessingLog, setExpandProcessingLog] = useState(false);
  const [processingLogCopied, setProcessingLogCopied] = useState(false);
  const [compareMode, setCompareMode] = useState('side_by_side');
  const [compareZoom, setCompareZoom] = useState(1);
  const [comparePan, setComparePan] = useState({ x: 0, y: 0 });
  const [compareSlider, setCompareSlider] = useState(50);
  const [compareDragging, setCompareDragging] = useState(false);
  const compareDragRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const [error, setError] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const previewUrlsRef = useRef(new Set());
  const resultRef = useRef(null);
  const wasActiveRef = useRef(false);

  const isActive = mosaic && ['queued', 'processing'].includes(mosaic.status);
  const isFinished = mosaic && ['completed', 'partial_success'].includes(mosaic.status);
  const displayStatus = !mosaic
    ? 'Ready'
    : isFinished
      ? 'Done'
      : mosaic.status === 'processing'
        ? 'Processing'
        : mosaic.status === 'queued'
          ? 'Queued'
          : mosaic.status === 'failed'
            ? 'Failed'
            : mosaic.status;
  const hasResult = Boolean(mosaic?.output_url);
  const hasCandidate = Boolean(mosaic?.candidate_available && mosaic?.candidate_output_url);
  const processingLog = mosaic?.processing_log || [];
  const manualPairResults = processingLog.filter((entry) => entry.event === 'manual_pair_result');

  const activityLabel = (entry) => {
    const imageName = (key, indexKey) => {
      const internalName = entry[key] || (entry[indexKey] !== undefined ? `Image ${Number(entry[indexKey]) + 1}` : null);
      const originalName = entry[indexKey] !== undefined ? activeItems[Number(entry[indexKey])]?.name : null;
      return originalName && internalName && originalName !== internalName
        ? `${internalName} (${originalName})`
        : (originalName || internalName);
    };

    if (entry.event === 'manual_pair_result') {
      const from = imageName('filename_a', 'image_a');
      const to = imageName('filename_b', 'image_b');
      const outcome = !entry.accepted
        ? `Rejected${entry.reason ? ` (${String(entry.reason).replace(/_/g, ' ')})` : ''}`
        : entry.used_for_initial_placement
          ? 'Accepted and used during initial placement'
          : entry.connected_in_output
            ? 'Accepted; both images belong to one output region'
            : 'Accepted but images remain in separate output regions';
      return `Suggested pair ${from} ↔ ${to}: ${outcome}; ${entry.already_tested_automatically ? 'already tested automatically' : 'new candidate'}`;
    }
    if (entry.event === 'pair_start') {
      return `Trying ${imageName('filename_a', 'image_a')} ↔ ${imageName('filename_b', 'image_b')}`;
    }
    if (entry.event === 'pair_accepted') {
      const details = entry.inliers !== undefined ? ` — ${entry.inliers} verified matching points` : '';
      return `Matched ${imageName('filename_a', 'image_a')} ↔ ${imageName('filename_b', 'image_b')}${details}`;
    }
    if (entry.event === 'pair_rejected') {
      const reason = entry.reason ? ` (${String(entry.reason).replaceAll('_', ' ')})` : '';
      return `No usable match for ${imageName('filename_a', 'image_a')} ↔ ${imageName('filename_b', 'image_b')}${reason}`;
    }
    if (entry.event === 'summary') {
      const rejected = (entry.rejected_image_indices || [])
        .map((index) => activeItems[Number(index)]?.name)
        .filter(Boolean);
      const counts = entry.candidate_counts_by_type || {};
      const timings = entry.candidate_matching_seconds_by_type || {};
      const timingParts = [
        counts.temporal !== undefined
          ? `nearby: ${counts.temporal} pairs / ${Number(timings.temporal || 0).toFixed(2)} s`
          : null,
        counts.appearance !== undefined
          ? `distant: ${counts.appearance} pairs / ${Number(timings.appearance || 0).toFixed(2)} s`
          : null,
        counts.hint
          ? `manual: ${counts.hint} pairs / ${Number(timings.hint || 0).toFixed(2)} s`
          : null,
      ].filter(Boolean);
      const parts = [entry.message];
      if (timingParts.length > 0) parts.push(`Matching time — ${timingParts.join('; ')}`);
      if (rejected.length > 0) parts.push(`Not used: ${rejected.join(', ')}`);
      return parts.join('. ');
    }
    return entry.message || entry.event || 'Processing';
  };

  const copyProcessingLog = async () => {
    const text = processingLog.map((entry) => activityLabel(entry)).join('\n');
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setProcessingLogCopied(true);
      window.setTimeout(() => setProcessingLogCopied(false), 2000);
    } catch (copyError) {
      setError('Could not copy the processing log to the clipboard.');
    }
  };

  const renderProcessingLog = () => (
    <div className="m-top-1">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <button
          className="btn btn-secondary radius"
          type="button"
          onClick={() => setShowProcessingLog((value) => !value)}
          style={{ margin: 0 }}
        >
          <i className={`bx ${showProcessingLog ? 'bx-chevron-up' : 'bx-chevron-down'}`} aria-hidden="true" />{' '}
          {showProcessingLog ? 'Hide Processing Log' : 'Show Processing Log'}
          {processingLog.length > 0 ? ` (${processingLog.length})` : ''}
        </button>
        {showProcessingLog && (
          <button
            className="btn btn-secondary radius"
            type="button"
            onClick={() => setExpandProcessingLog((value) => !value)}
            style={{ margin: 0 }}
          >
            {expandProcessingLog ? 'Collapse Log' : 'Expand Log'}
          </button>
        )}
        <button
          className="btn btn-secondary radius"
          type="button"
          onClick={copyProcessingLog}
          disabled={processingLog.length === 0}
          style={{ margin: 0 }}
        >
          <i className={`bx ${processingLogCopied ? 'bx-check' : 'bx-copy'}`} aria-hidden="true" />{' '}
          {processingLogCopied ? 'Copied!' : 'Copy to Clipboard'}
        </button>
      </div>
      {showProcessingLog && (
        <>
          <div className="m-top-half" style={{ ...styles.processingLog, maxHeight: expandProcessingLog ? 'none' : styles.processingLog.maxHeight }}>
          {processingLog.length === 0 ? (
            <div className="small-text inactive-text">
              {isActive ? 'Waiting for matching activity…' : 'No processing activity was recorded for this run.'}
            </div>
          ) : (
            processingLog.map((entry, index) => (
              <div key={`${entry.event || 'event'}-${index}`} style={styles.processingLogRow}>
                <span style={styles.processingLogIcon}>
                  {entry.event === 'pair_accepted' ? '✓' : entry.event === 'pair_rejected' ? '–' : '•'}
                </span>
                <span>{activityLabel(entry)}</span>
              </div>
            ))
          )}
          </div>
        </>
      )}
    </div>
  );

  const formatSettingValue = (key, value) => {
    if (key === 'preset') return PRESET_LABELS[value] || value || '—';
    if (typeof value === 'boolean') return value ? 'On' : 'Off';
    if (value === null || value === undefined || value === '') return '—';
    if (key === 'reprojection_threshold_px' || key === 'max_dimension') return `${value} px`;
    return String(value);
  };

  const settingChanges = useMemo(() => {
    const previous = mosaic?.published_settings || {};
    const current = mosaic?.settings || {};
    if (!Object.keys(previous).length) return [];

    // Older mosaics predate calibration settings. Treat missing calibration
    // values as today's defaults so compare mode only reports real changes.
    const normalizedPrevious = { ...CAMERA_CALIBRATION_DEFAULTS, ...previous };
    const normalizedCurrent = { ...CAMERA_CALIBRATION_DEFAULTS, ...current };

    return Object.keys(SETTING_LABELS)
      .filter((key) => normalizedPrevious[key] !== normalizedCurrent[key])
      .map((key) => ({
        key,
        label: SETTING_LABELS[key],
        previous: formatSettingValue(key, normalizedPrevious[key]),
        current: formatSettingValue(key, normalizedCurrent[key]),
      }));
  }, [mosaic?.published_settings, mosaic?.settings]);

  useEffect(() => {
    endpoint
      .get('/client_api/v1/mixed_medias/image_mosaic_settings')
      .then((response) => {
        setSettingsMeta(response.data);
        const balanced = response.data?.presets?.balanced;
        if (!balanced) return;

        setSettings((current) => {
          const untouched =
            current.preset === 'balanced' &&
            Object.keys(FALLBACK_PRESETS.balanced).every(
              (key) => current[key] === FALLBACK_PRESETS.balanced[key]
            );

          return untouched
            ? { ...current, ...balanced, preset: 'balanced' }
            : current;
        });
      })
      .catch(() => {
        // Keep local defaults if the CPU service is not reachable while authoring.
      });
  }, []);

  useEffect(() => {
    if (!mosaic) return;

    const available = mosaic.source_images || [];
    const order = mosaic.image_order && mosaic.image_order.length
      ? mosaic.image_order
      : available.map((image) => image.original_index);
    const rank = new Map(order.map((value, index) => [value, index]));

    setItems(
      available
        .map((image) => ({
          originalIndex: image.original_index,
          name: image.filename,
          previewUrl: image.url,
          file: null,
          valid: true,
        }))
        .sort((a, b) => {
          const ar = rank.has(a.originalIndex) ? rank.get(a.originalIndex) : Number.MAX_SAFE_INTEGER;
          const br = rank.has(b.originalIndex) ? rank.get(b.originalIndex) : Number.MAX_SAFE_INTEGER;
          return ar - br || a.originalIndex - b.originalIndex;
        })
    );
    setExcluded(new Set(mosaic.excluded_images || []));
    setHints(mosaic.adjacency_hints || []);
    if (mosaic.settings && Object.keys(mosaic.settings).length) {
      const persistedSettings = { ...mosaic.settings };
      if (persistedSettings.max_dimension === undefined && persistedSettings.working_height !== undefined) {
        persistedSettings.max_dimension = persistedSettings.working_height;
      }
      delete persistedSettings.working_height;
      setSettings((current) => ({ ...current, ...persistedSettings }));
    }
  }, [mosaic && mosaic.id]);

  useEffect(() => {
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = Boolean(isActive);

    const completedSuccessfully =
      hasCandidate ||
      (isFinished && hasResult && !mosaic?.error_message);

    if (wasActive && !isActive && completedSuccessfully) {
      window.requestAnimationFrame(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [isActive, isFinished, hasResult, hasCandidate, mosaic?.error_message]);

  useEffect(() => {
    if (!media || !isActive) return undefined;

    const interval = window.setInterval(() => {
      endpoint
        .get(`/client_api/v1/mixed_medias/${media.id}`)
        .then((response) => {
          setMosaic(response.data.image_mosaic);
          if (response.data.status !== undefined) setStatus(response.data.status);
          if (response.data.completed !== undefined) setCompleted(response.data.completed);
        })
        .catch(() => {
          // A transient polling problem should not discard an active job.
        });
    }, 5000);

    return () => window.clearInterval(interval);
  }, [media && media.id, isActive]);

  const revokePreviewUrls = () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
  };

  useEffect(() => () => revokePreviewUrls(), []);

  const resetMosaic = () => {
    revokePreviewUrls();
    setMedia(null);
    setMosaic(null);
    setItems([]);
    setExcluded(new Set());
    setHints([]);
    setHintFrom('');
    setHintTo('');
    setError(null);
    setCalibrationFileName('');
    setCalibrationReprojectionError(null);
    setUploadProgress({ completed: 0, total: 0 });
    setAdvanced(false);
    setShowReplaceWarning(false);
    setLargeResultUrl(null);
    setSettings({
      preset: 'balanced',
      ...(settingsMeta.presets?.balanced || FALLBACK_PRESETS.balanced),
      ...CAMERA_CALIBRATION_DEFAULTS,
    });
  };

  const validateBrowserImage = (item) =>
    new Promise((resolve) => {
      const image = new Image();

      image.onload = () => {
        resolve({
          ...item,
          valid: image.naturalWidth > 0 && image.naturalHeight > 0,
        });
      };
      image.onerror = () => resolve({ ...item, valid: false });
      image.src = item.previewUrl;
    });

  const selectFiles = async (fileList) => {
    revokePreviewUrls();
    const files = Array.from(fileList || []);
    const mapped = files.map((file, index) => {
      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.add(previewUrl);
      return {
        originalIndex: index,
        name: file.name,
        previewUrl,
        file,
        valid: null,
      };
    });

    setItems(sortImagesByFilename(mapped));
    setExcluded(new Set());
    setHints([]);
    setHintFrom('');
    setHintTo('');
    setMosaic(null);
    setMedia(null);
    setError(null);
    setShowReplaceWarning(false);

    const validated = await Promise.all(mapped.map(validateBrowserImage));
    const invalidIndices = validated
      .filter((item) => item.valid === false)
      .map((item) => item.originalIndex);

    setItems(sortImagesByFilename(validated));
    setExcluded(new Set(invalidIndices));
  };

  const selectedBytes = () =>
    items.reduce((sum, item) => sum + (item.file?.size || 0), 0);

  const invalidItems = items.filter((item) => item.valid === false);
  const validatingItems = items.filter((item) => item.valid === null);
  const validItems = items.filter((item) => item.valid !== false);
  const activeItems = validItems.filter((item) => !excluded.has(item.originalIndex));
  const imageOrder = activeItems.map((item) => item.originalIndex);

  const validateSettingRange = (key) => {
    const field = {
      ...(FALLBACK_SETTING_FIELDS[key] || {}),
      ...(settingsMeta.fields?.[key] || {}),
    };
    const value = Number(settings[key]);
    if (!Number.isFinite(value)) return null;

    if (field.minimum !== undefined && value < field.minimum) {
      return `${field.label || SETTING_LABELS[key] || key} must be at least ${field.minimum}.`;
    }
    if (field.maximum !== undefined && value > field.maximum) {
      return `${field.label || SETTING_LABELS[key] || key} must be no more than ${field.maximum}.`;
    }
    return null;
  };

  const settingsRangeError = [
    'max_dimension',
    'feature_match_ratio',
    'min_good_matches',
    'reprojection_threshold_px',
    'neighbor_radius',
    'nonlocal_candidates_per_frame',
  ].map((key) => validateSettingRange(key)).find(Boolean) || null;

  const cameraCalibrationError = (() => {
    if (!settings.camera_calibration_enabled) return null;

    const required = [
      ['camera_fx', 'Camera fx'],
      ['camera_fy', 'Camera fy'],
      ['camera_cx', 'Camera cx'],
      ['camera_cy', 'Camera cy'],
      ['camera_k1', 'Distortion k1'],
      ['camera_k2', 'Distortion k2'],
      ...(settings.camera_model === 'fisheye'
        ? [['camera_k3', 'Fisheye k3'], ['camera_k4', 'Fisheye k4']]
        : [['camera_p1', 'Tangential p1'], ['camera_p2', 'Tangential p2']]),
    ];
    const missing = required
      .filter(([key]) => settings[key] === null || settings[key] === undefined || settings[key] === '')
      .map(([, label]) => label);
    if (missing.length) return `Enter all camera calibration values: ${missing.join(', ')}.`;

    if (Number(settings.camera_fx) <= 0 || Number(settings.camera_fy) <= 0) {
      return 'Camera fx and fy must be greater than 0.';
    }
    if (Number(settings.camera_cx) < 0 || Number(settings.camera_cy) < 0) {
      return 'Camera cx and cy cannot be negative.';
    }

    const widthSet = settings.camera_calibration_width !== null &&
      settings.camera_calibration_width !== undefined &&
      settings.camera_calibration_width !== '';
    const heightSet = settings.camera_calibration_height !== null &&
      settings.camera_calibration_height !== undefined &&
      settings.camera_calibration_height !== '';
    if (widthSet !== heightSet) {
      return 'Enter both calibration image width and height, or leave both blank.';
    }
    if (widthSet && (Number(settings.camera_calibration_width) < 1 || Number(settings.camera_calibration_height) < 1)) {
      return 'Calibration image width and height must be greater than 0.';
    }

    const balance = Number(settings.camera_balance ?? 0);
    if (!Number.isFinite(balance) || balance < 0 || balance > 1) {
      return 'Lens correction balance must be between 0 and 1.';
    }
    return null;
  })();

  const validate = () => {
    if (!mosaic) {
      if (validatingItems.length > 0) return 'Please wait while the selected images are checked.';
      if (validItems.length < 2) return 'Please select at least 2 valid images.';
      if (maxImages && items.length > maxImages) {
        return `Please select no more than ${maxImages} images.`;
      }
      if (items.some((item) => !['jpg', 'jpeg', 'png'].includes(item.name.split('.').pop().toLowerCase()))) {
        return 'Images must be JPG, JPEG, or PNG files.';
      }
      if (maxUploadSizeBytes && selectedBytes() > maxUploadSizeBytes) {
        return `The image set must be ${maxUploadSizeMb || 60} MB or smaller in total.`;
      }
    }
    if (activeItems.length < 2) return 'At least 2 images must remain enabled.';

    for (const key of [
      'max_dimension',
      'feature_match_ratio',
      'min_good_matches',
      'reprojection_threshold_px',
      'neighbor_radius',
      'nonlocal_candidates_per_frame',
    ]) {
      const rangeError = validateSettingRange(key);
      if (rangeError) return rangeError;
    }

    if (cameraCalibrationError) return cameraCalibrationError;
    return null;
  };

  const applyPreset = (preset) => {
    if (preset === 'custom') {
      setSettings((current) => ({ ...current, preset: 'custom' }));
      return;
    }
    const presetValues = settingsMeta.presets?.[preset] || FALLBACK_PRESETS[preset] || {};
    setSettings((current) => ({
      ...current,
      preset,
      ...presetValues,
    }));
  };

  const updateSetting = (name, value) => {
    setSettings((current) => ({
      ...current,
      preset: 'custom',
      [name]: value,
    }));
  };

  const importCalibrationJson = async (file) => {
    if (!file) return;

    try {
      const parsed = JSON.parse(await file.text());
      const rawMatrix = parsed.camera_matrix;
      const matrix = Array.isArray(rawMatrix?.[0])
        ? rawMatrix.flat()
        : rawMatrix;
      const rawCoefficients = parsed.distortion_coefficients;
      const coefficients = Array.isArray(rawCoefficients?.[0])
        ? rawCoefficients.flat()
        : rawCoefficients;
      const imageSize = parsed.image_size || {};
      const rawModel = parsed.camera_model || parsed.distortion_model || parsed.model || 'standard';
      const normalizedModel = String(rawModel).trim().toLowerCase().replaceAll('-', '_');
      const fisheyeModels = ['fisheye', 'opencv_fisheye'];
      const standardModels = ['standard', 'opencv', 'pinhole', 'rational', 'opencv_standard'];

      if (!Array.isArray(matrix) || matrix.length !== 9) {
        throw new Error('camera_matrix must contain 9 values.');
      }
      if (!Array.isArray(coefficients)) {
        throw new Error('distortion_coefficients must be an array.');
      }
      if (![...fisheyeModels, ...standardModels].includes(normalizedModel)) {
        throw new Error(`Unsupported camera model "${rawModel}".`);
      }

      const fisheye = fisheyeModels.includes(normalizedModel);
      const allowedLengths = fisheye ? [4] : [4, 5, 8];
      if (!allowedLengths.includes(coefficients.length)) {
        throw new Error(
          fisheye
            ? 'Fisheye distortion_coefficients must contain 4 values.'
            : 'Standard OpenCV distortion_coefficients must contain 4, 5, or 8 values.'
        );
      }

      const rawNumericValues = [...matrix, ...coefficients];
      if (rawNumericValues.some((value) => (
        typeof value !== 'number' || !Number.isFinite(value)
      ))) {
        throw new Error('The calibration file contains non-numeric camera values.');
      }

      const hasImageWidth = Object.prototype.hasOwnProperty.call(imageSize, 'width');
      const hasImageHeight = Object.prototype.hasOwnProperty.call(imageSize, 'height');
      if (hasImageWidth !== hasImageHeight) {
        throw new Error('image_size must include both width and height.');
      }

      let calibrationWidth = null;
      let calibrationHeight = null;
      if (hasImageWidth && hasImageHeight) {
        if (
          typeof imageSize.width !== 'number' ||
          typeof imageSize.height !== 'number' ||
          !Number.isFinite(imageSize.width) ||
          !Number.isFinite(imageSize.height) ||
          imageSize.width <= 0 ||
          imageSize.height <= 0
        ) {
          throw new Error('image_size width and height must be positive numbers.');
        }
        calibrationWidth = imageSize.width;
        calibrationHeight = imageSize.height;
      }

      const numericMatrix = matrix;
      const numericCoefficients = coefficients;

      const [
        k1,
        k2,
        coefficient3,
        coefficient4,
        coefficient5 = null,
        coefficient6 = null,
        coefficient7 = null,
        coefficient8 = null,
      ] = numericCoefficients;

      setSettings((current) => ({
        ...current,
        preset: 'custom',
        camera_calibration_enabled: true,
        camera_model: fisheye ? 'fisheye' : 'standard',
        camera_fx: numericMatrix[0],
        camera_fy: numericMatrix[4],
        camera_cx: numericMatrix[2],
        camera_cy: numericMatrix[5],
        camera_k1: k1,
        camera_k2: k2,
        camera_p1: fisheye ? null : coefficient3,
        camera_p2: fisheye ? null : coefficient4,
        camera_k3: fisheye ? coefficient3 : coefficient5,
        camera_k4: fisheye ? coefficient4 : coefficient6,
        camera_k5: fisheye ? null : coefficient7,
        camera_k6: fisheye ? null : coefficient8,
        camera_calibration_width: calibrationWidth,
        camera_calibration_height: calibrationHeight,
      }));
      setCalibrationFileName(file.name);
      setCalibrationReprojectionError(
        Number.isFinite(Number(parsed.avg_reprojection_error))
          ? Number(parsed.avg_reprojection_error)
          : null
      );
      setError(null);
    } catch (importError) {
      setCalibrationFileName('');
      setCalibrationReprojectionError(null);
      setError(`Could not import calibration JSON: ${importError.message}`);
    }
  };

  const settingField = (name, fallback = {}) => ({
    ...(FALLBACK_SETTING_FIELDS[name] || {}),
    ...fallback,
    ...(settingsMeta.fields?.[name] || {}),
  });

  const positionHelpTip = (event, text) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportPadding = 12;
    const tooltipWidth = Math.min(280, window.innerWidth - (viewportPadding * 2));
    const estimatedHeight = 84;

    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipWidth - viewportPadding));

    const spaceAbove = rect.top;
    const placeBelow = spaceAbove < estimatedHeight + 16;
    const top = placeBelow
      ? rect.bottom + 8
      : rect.top - estimatedHeight - 8;

    setActiveHelpTip(text);
    setHelpTipPosition({
      left,
      top: Math.max(viewportPadding, top),
      width: tooltipWidth,
    });
  };

  const closeHelpTip = () => {
    setActiveHelpTip(null);
    setHelpTipPosition(null);
  };

  const helpTip = (text) => {
    const open = activeHelpTip === text && helpTipPosition;

    return (
      <span
        style={styles.helpTip}
        tabIndex="0"
        role="button"
        aria-label={text}
        aria-expanded={Boolean(open)}
        onMouseEnter={(event) => positionHelpTip(event, text)}
        onMouseLeave={closeHelpTip}
        onFocus={(event) => positionHelpTip(event, text)}
        onBlur={closeHelpTip}
      >
        <i className="bx bxs-info-circle" aria-hidden="true" />
        {open && (
          <span
            role="tooltip"
            style={{
              ...styles.helpBubble,
              left: helpTipPosition.left,
              top: helpTipPosition.top,
              width: helpTipPosition.width,
            }}
          >
            {text}
          </span>
        )}
      </span>
    );
  };

  const fieldLabel = (label, help, range = null) => (
    <span style={styles.fieldLabel}>
      <span style={styles.fieldName}>
        <span>{label}</span>
        {helpTip(help)}
      </span>
      {range && <span className="small-text inactive-text" style={styles.fieldRange}>{range}</span>}
    </span>
  );

  const clampSetting = (name) => {
    const field = settingField(name);
    const value = Number(settings[name]);
    if (!Number.isFinite(value)) return;

    let next = value;
    if (field.minimum !== undefined) next = Math.max(field.minimum, next);
    if (field.maximum !== undefined) next = Math.min(field.maximum, next);

    if (next !== value) updateSetting(name, next);
  };

  const toggleExcluded = (originalIndex) => {
    const item = items.find((candidate) => candidate.originalIndex === originalIndex);
    if (item?.valid === false) return;
    setExcluded((current) => {
      const next = new Set(current);
      if (next.has(originalIndex)) next.delete(originalIndex);
      else next.add(originalIndex);
      return next;
    });
    setHints((current) =>
      current.filter((pair) => !pair.includes(originalIndex))
    );
  };

  const addOverlapHint = () => {
    if (hintFrom === '' || hintTo === '') return;
    const first = Number(hintFrom);
    const second = Number(hintTo);
    if (first === second) return;

    const pair = [first, second].sort((a, b) => a - b);
    setHints((current) => {
      const exists = current.some(
        (existing) => existing.length === 2 && existing[0] === pair[0] && existing[1] === pair[1]
      );
      return exists ? current : [...current, pair];
    });
    setHintFrom('');
    setHintTo('');
  };

  const removeOverlapHint = (pair) => {
    setHints((current) =>
      current.filter(
        (existing) => !(existing.length === 2 && existing[0] === pair[0] && existing[1] === pair[1])
      )
    );
  };

  const restoreFilenameOrder = () => {
    const sorted = sortImagesByFilename(items);
    if (sorted.every((item, index) => item.originalIndex === items[index].originalIndex)) return;

    if (!window.confirm(
      'Sort images by filename? This will replace your current manual image order. You can cancel to keep your arrangement.'
    )) return;

    setItems(sorted);
  };

  const clearImageDrag = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Preview the proposed capture order without saving it until drop.
  const previewItems = [...items];
  if (draggedIndex !== null && dragOverIndex !== null) {
    const [dragged] = previewItems.splice(draggedIndex, 1);
    previewItems.splice(dragOverIndex, 0, dragged);
  }

  const moveDraggedItem = (targetIndex) => {
    if (draggedIndex !== null && draggedIndex !== targetIndex) {
      setItems((current) => {
        const next = [...current];
        const [moved] = next.splice(draggedIndex, 1);
        next.splice(targetIndex, 0, moved);
        return next;
      });
    }
    clearImageDrag();
  };

  const checksumFile = (file) =>
    new Promise((resolve, reject) => {
      if (!window.CryptoJS?.algo?.MD5) {
        reject(new Error('The browser checksum helper is unavailable.'));
        return;
      }

      const chunkSize = 2 * 1024 * 1024;
      const hasher = window.CryptoJS.algo.MD5.create();
      const reader = new FileReader();
      let offset = 0;

      const readNext = () => {
        const slice = file.slice(offset, Math.min(offset + chunkSize, file.size));
        reader.readAsArrayBuffer(slice);
      };

      reader.onerror = () => reject(reader.error || new Error('Unable to read image for upload.'));
      reader.onload = (event) => {
        const bytes = new Uint8Array(event.target.result);
        hasher.update(window.CryptoJS.lib.WordArray.create(bytes));
        offset += bytes.byteLength;

        if (offset < file.size) {
          readNext();
          return;
        }

        resolve(window.CryptoJS.enc.Base64.stringify(hasher.finalize()));
      };

      readNext();
    });

  const uploadImageDirectly = async (item) => {
    const file = item.file;
    const checksum = await checksumFile(file);
    const metadataResponse = await endpoint.post(
      '/client_api/v1/mixed_medias/image_mosaic_direct_upload',
      {
        blob: {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          byte_size: file.size,
          checksum,
        },
      }
    );

    const { signed_id: signedId, direct_upload: directUpload } = metadataResponse.data;
    await axios.put(directUpload.url, file, {
      headers: directUpload.headers,
      transformRequest: [(data) => data],
    });

    return signedId;
  };

  const uploadImagesDirectly = async (orderedItems) => {
    const signedIds = new Array(orderedItems.length);
    let nextIndex = 0;
    let completed = 0;
    const workerCount = Math.min(4, orderedItems.length);

    const worker = async () => {
      while (nextIndex < orderedItems.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        signedIds[currentIndex] = await uploadImageDirectly(orderedItems[currentIndex]);
        completed += 1;
        setUploadProgress({ completed, total: orderedItems.length });
      }
    };

    const workers = Array.from({ length: workerCount }, () => worker());
    const results = await Promise.allSettled(workers);
    const failedWorker = results.find((result) => result.status === 'rejected');
    if (failedWorker) throw failedWorker.reason;
    return signedIds;
  };

  const submitNew = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setUploadProgress({ completed: 0, total: validItems.length });
    setError(null);

    try {
      const orderedItems = validItems
        .slice()
        .sort((a, b) => a.originalIndex - b.originalIndex);
      const indexMap = new Map(
        orderedItems.map((item, newIndex) => [item.originalIndex, newIndex])
      );
      const remappedImageOrder = imageOrder
        .filter((index) => indexMap.has(index))
        .map((index) => indexMap.get(index));
      const remappedExcluded = Array.from(excluded)
        .filter((index) => indexMap.has(index))
        .map((index) => indexMap.get(index));
      const remappedHints = hints
        .filter((pair) => indexMap.has(pair[0]) && indexMap.has(pair[1]))
        .map((pair) => [indexMap.get(pair[0]), indexMap.get(pair[1])]);

      setUploadProgress({ completed: 0, total: orderedItems.length });
      const signedIds = await uploadImagesDirectly(orderedItems);
      setUploadProgress({ completed: signedIds.length, total: 0 });
      const response = await endpoint.post('/client_api/v1/mixed_medias', {
        requirement_id: requirement.id,
        mixed_media: {
          description: 'Image mosaic source images',
        },
        image_blob_signed_ids: signedIds,
        image_mosaic_settings: JSON.stringify(settings),
        image_order: JSON.stringify(remappedImageOrder),
        excluded_images: JSON.stringify(remappedExcluded),
        adjacency_hints: JSON.stringify(remappedHints),
      });

      setMedia(response.data.media);
      setMosaic(response.data.image_mosaic);
      if (response.data.status !== undefined) setStatus(response.data.status);
      setCompleted(false);
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'The image mosaic could not be started.');
    } finally {
      setUploading(false);
      setUploadProgress({ completed: 0, total: 0 });
    }
  };

  const reprocess = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!media) return;

    setUploading(true);
    setError(null);
    endpoint
      .post(`/client_api/v1/mixed_medias/${media.id}/reprocess_image_mosaic`, {
        image_mosaic_settings: JSON.stringify(settings),
        image_order: JSON.stringify(imageOrder),
        excluded_images: JSON.stringify(Array.from(excluded)),
        adjacency_hints: JSON.stringify(hints),
      })
      .then((response) => {
        setMosaic(response.data.image_mosaic);
        if (response.data.completed !== undefined) setCompleted(response.data.completed);
        setUploading(false);
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.error || 'The image mosaic could not be reprocessed.');
        setUploading(false);
      });
  };

  const cancelProcessingAndStartOver = () => {
    if (!media || !isActive) {
      resetMosaic();
      return;
    }

    const message = mosaic?.reprocessing
      ? 'Stop this reprocessing job and start over? Your current mosaic will be kept.'
      : 'Stop this processing job and start over?';

    if (!window.confirm(message)) {
      return;
    }

    setUploading(true);
    setError(null);
    endpoint
      .post(`/client_api/v1/mixed_medias/${media.id}/cancel_image_mosaic_reprocessing`)
      .then(() => {
        setUploading(false);
        resetMosaic();
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.error || 'The processing job could not be canceled.');
        setUploading(false);
      });
  };

  const resolveCandidate = (action) => {
    if (!media || !hasCandidate) return;

    setUploading(true);
    setError(null);
    endpoint
      .post(`/client_api/v1/mixed_medias/${media.id}/${action}_image_mosaic_candidate`)
      .then((response) => {
        setMosaic(response.data.image_mosaic);
        if (response.data.status !== undefined) setStatus(response.data.status);
        if (response.data.completed !== undefined) setCompleted(response.data.completed);
        setUploading(false);
      })
      .catch((requestError) => {
        setError(
          requestError.response?.data?.error ||
            `The reprocessed mosaic could not be ${action === 'accept' ? 'used' : 'discarded'}.`
        );
        setUploading(false);
      });
  };

  const openCompareViewer = () => {
    setCompareMode('side_by_side');
    setCompareZoom(1);
    setComparePan({ x: 0, y: 0 });
    setCompareSlider(50);
    setShowCompareViewer(true);
  };

  const resetCompareView = () => {
    setCompareZoom(1);
    setComparePan({ x: 0, y: 0 });
    setCompareSlider(50);
  };

  const handleCompareWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.15 : 0.15;
    setCompareZoom((value) => Math.min(5, Math.max(1, Number((value + delta).toFixed(2)))));
  };

  const startComparePan = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    compareDragRef.current = {
      x: event.clientX,
      y: event.clientY,
      panX: comparePan.x,
      panY: comparePan.y,
    };
    setCompareDragging(true);
  };

  const moveComparePan = (event) => {
    if (!compareDragging) return;
    setComparePan({
      x: compareDragRef.current.panX + (event.clientX - compareDragRef.current.x),
      y: compareDragRef.current.panY + (event.clientY - compareDragRef.current.y),
    });
  };

  const stopComparePan = () => setCompareDragging(false);

  const styles = {
    shell: {
      border: '1px solid #e1e5ea',
      borderRadius: '12px',
      background: '#fff',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      padding: '15px 18px',
      background: '#f8f9fb',
      borderBottom: '1px solid #eceff2',
    },
    statusPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      borderRadius: '999px',
      padding: '5px 10px',
      background: isFinished
        ? '#e9f8ee'
        : mosaic?.status === 'failed'
          ? '#fdecec'
          : mosaic
            ? '#edf4ff'
            : '#f0f2f5',
      color: isFinished
        ? '#237a3b'
        : mosaic?.status === 'failed'
          ? '#a33636'
          : mosaic
            ? '#315f9b'
            : '#59636f',
      fontSize: '12px',
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },
    body: { padding: '18px' },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '12px',
      marginTop: '14px',
    },
    card: {
      position: 'relative',
      border: '1px solid #dfe4ea',
      borderRadius: '9px',
      background: '#fff',
      padding: '8px',
      cursor: 'grab',
      transition: 'border-color 150ms ease, box-shadow 150ms ease, opacity 150ms ease',
    },
    selectedCard: {
      borderColor: '#2688d8',
      boxShadow: '0 0 0 1px rgba(38, 136, 216, 0.18)',
    },
    invalidCard: {
      borderColor: '#cf5a5a',
      background: '#fff7f7',
      cursor: 'default',
    },
    invalidPreview: {
      width: '100%',
      aspectRatio: '4 / 3',
      borderRadius: '6px',
      background: '#f3e8e8',
      color: '#9b3f3f',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '6px',
      textAlign: 'center',
      padding: '10px',
      boxSizing: 'border-box',
    },
    selectionBadge: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      zIndex: 2,
      width: '24px',
      height: '24px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px solid #fff',
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.22)',
      cursor: 'pointer',
      padding: 0,
      lineHeight: 1,
    },
    thumb: {
      width: '100%',
      aspectRatio: '4 / 3',
      objectFit: 'cover',
      borderRadius: '6px',
      background: '#eef1f4',
    },
    controls: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
      gap: '12px',
      marginTop: '12px',
    },
    advancedPanel: {
      marginTop: '10px',
      padding: '14px',
      border: '1px solid #e1e5ea',
      borderRadius: '10px',
      background: '#f8f9fb',
    },
    advancedGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '14px 16px',
      alignItems: 'end',
    },
    field: {
      display: 'grid',
      gridTemplateRows: '42px 38px',
      gap: '6px',
      margin: 0,
      fontSize: '13px',
      fontWeight: 600,
      color: '#343b44',
      alignSelf: 'end',
    },
    fieldLabel: {
      minHeight: '42px',
      display: 'flex',
      flexWrap: 'wrap',
      alignContent: 'flex-end',
      alignItems: 'baseline',
      columnGap: '5px',
      rowGap: 0,
      lineHeight: 1.35,
    },
    fieldName: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      maxWidth: '100%',
    },
    fieldRange: {
      whiteSpace: 'normal',
    },
    helpTip: {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '0 0 auto',
      cursor: 'help',
      fontSize: '14px',
      lineHeight: 1,
      marginBottom: '1px',
      outline: 'none',
      zIndex: 20,
    },
    helpBubble: {
      position: 'fixed',
      zIndex: 9999,
      padding: '8px 10px',
      borderRadius: '6px',
      background: '#2f343a',
      color: '#fff',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.35,
      textAlign: 'left',
      boxShadow: '0 3px 10px rgba(0, 0, 0, 0.22)',
      pointerEvents: 'none',
      whiteSpace: 'normal',
      boxSizing: 'border-box',
    },
    fileField: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      margin: 0,
      fontSize: '13px',
      fontWeight: 600,
      color: '#343b44',
      alignSelf: 'end',
    },
    fileInput: {
      display: 'block',
      width: '100%',
      minHeight: '38px',
      height: '38px',
      margin: 0,
      padding: '4px',
      lineHeight: 'normal',
      boxSizing: 'border-box',
      overflow: 'visible',
    },
    checkboxLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
    },
    checkboxToggleLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      margin: 0,
      cursor: 'pointer',
    },
    fieldControl: {
      width: '100%',
      margin: 0,
      minHeight: '38px',
      boxSizing: 'border-box',
    },
    checkboxField: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      margin: 0,
      minHeight: '38px',
      paddingTop: 0,
      alignSelf: 'end',
      fontSize: '13px',
      fontWeight: 600,
      color: '#343b44',
    },
    processingLog: {
      maxHeight: '280px',
      overflowY: 'auto',
      border: '1px solid #dfe4ea',
      borderRadius: '8px',
      background: '#f8f9fb',
      padding: '10px 12px',
    },
    processingLogRow: {
      display: 'grid',
      gridTemplateColumns: '18px minmax(0, 1fr)',
      gap: '6px',
      padding: '4px 0',
      fontSize: '12px',
      lineHeight: 1.35,
      borderBottom: '1px solid #edf0f3',
    },
    processingLogIcon: {
      textAlign: 'center',
      fontWeight: 700,
    },
    processingActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap',
      marginTop: '16px',
    },
    resultImage: {
      display: 'block',
      width: '100%',
      maxHeight: '420px',
      objectFit: 'contain',
      border: '1px solid #dfe4ea',
      borderRadius: '10px',
      background: '#111',
      cursor: 'zoom-in',
    },
    compareGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '16px',
      marginTop: '12px',
    },
    compareCard: {
      border: '1px solid #dfe4ea',
      borderRadius: '10px',
      padding: '12px',
      background: '#f8f9fb',
    },
    compareLabel: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      marginBottom: '8px',
    },
    compareImage: {
      display: 'block',
      width: '100%',
      height: '280px',
      objectFit: 'contain',
      border: '1px solid #dfe4ea',
      borderRadius: '8px',
      background: '#111',
      cursor: 'zoom-in',
    },
    settingsCompare: {
      marginTop: '12px',
      border: '1px solid #dfe4ea',
      borderRadius: '10px',
      overflow: 'hidden',
    },
    settingsHeader: {
      display: 'grid',
      gridTemplateColumns: 'minmax(160px, 1.4fr) minmax(110px, 1fr) minmax(110px, 1fr)',
      gap: '10px',
      padding: '9px 12px',
      background: '#f1f3f5',
      fontSize: '12px',
      fontWeight: 700,
    },
    settingsRow: {
      display: 'grid',
      gridTemplateColumns: 'minmax(160px, 1.4fr) minmax(110px, 1fr) minmax(110px, 1fr)',
      gap: '10px',
      padding: '9px 12px',
      borderTop: '1px solid #e5e8eb',
      fontSize: '13px',
      alignItems: 'center',
    },
    pickerActions: {
      display: 'flex',
      justifyContent: 'center',
      gap: '10px',
      flexWrap: 'wrap',
      marginTop: '14px',
    },
    actions: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
      marginTop: '12px',
    },
    action: {
      flex: '1 1 220px',
      margin: 0,
    },
    overlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 10000,
      background: 'rgba(0, 0, 0, 0.82)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      cursor: 'zoom-out',
    },
    overlayImage: {
      display: 'block',
      maxWidth: '96vw',
      maxHeight: '92vh',
      objectFit: 'contain',
      borderRadius: '8px',
      background: '#111',
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.35)',
    },
    compareViewerOverlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 10020,
      background: 'rgba(12, 15, 18, 0.96)',
      display: 'flex',
      flexDirection: 'column',
      padding: '18px',
    },
    compareViewerHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      flexWrap: 'wrap',
      color: '#fff',
      marginBottom: '12px',
    },
    compareViewerControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap',
    },
    compareViewport: {
      position: 'relative',
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
      borderRadius: '10px',
      background: '#050607',
      border: '1px solid rgba(255,255,255,0.16)',
      userSelect: 'none',
      touchAction: 'none',
    },
    compareViewportGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1px',
      height: '100%',
      background: 'rgba(255,255,255,0.18)',
    },
    comparePane: {
      position: 'relative',
      overflow: 'hidden',
      background: '#050607',
      minWidth: 0,
    },
    comparePaneLabel: {
      position: 'absolute',
      top: '12px',
      left: '12px',
      zIndex: 5,
      padding: '6px 10px',
      borderRadius: '999px',
      background: 'rgba(0,0,0,0.68)',
      color: '#fff',
      fontSize: '12px',
      fontWeight: 700,
      pointerEvents: 'none',
    },
    compareViewerImage: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      transformOrigin: 'center center',
      pointerEvents: 'none',
    },
    sliderDivider: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: '2px',
      background: '#fff',
      zIndex: 6,
      boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
      pointerEvents: 'none',
    },
    compareHelp: {
      color: '#c9d1d9',
      fontSize: '12px',
      marginTop: '10px',
      textAlign: 'center',
    },
  };

  return (
    <>
      <style>{`
        .image-mosaic-download-button {
          background: #fff;
          color: #2367a7;
          border: 1px solid #9fb9d3;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          font-weight: 600;
          transition: background-color 140ms ease, border-color 140ms ease, color 140ms ease, box-shadow 140ms ease, transform 80ms ease;
        }

        .image-mosaic-download-button:hover {
          background: #eaf4ff;
          border-color: #6e9ec7;
          color: #174f82;
          box-shadow: 0 2px 5px rgba(35, 103, 167, 0.16);
        }

        .image-mosaic-download-button:active {
          background: #d7eaff;
          border-color: #4f84b5;
          color: #123f69;
          box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.12);
          transform: translateY(1px);
        }

        .image-mosaic-download-button:focus-visible {
          outline: 3px solid rgba(35, 103, 167, 0.28);
          outline-offset: 2px;
        }
      `}</style>
      <ElementContainer
      id={id}
      user={user}
      token={token}
      color={titleColor}
      title={title}
      requirable={requirable}
      reviewable={reviewable}
      status={status}
      completed={reviewable ? completed : (completed || isFinished)}
      icon={'bx-grid-alt'}
    >
      <div className="m-bottom-1">{prompt}</div>
      {error && <div className="danger radius pad-1 m-bottom-1">{error}</div>}

      <div style={styles.shell}>
        <div style={styles.header}>
          <h3 style={{ margin: 0, fontSize: '17px' }}>
            <i className="bx bx-grid-alt" aria-hidden="true" /> ImageMosaic
          </h3>
          <span style={styles.statusPill}>
            {displayStatus}
          </span>
        </div>

        <div style={styles.body}>
          <input
            ref={fileInputRef}
            style={{ display: 'none' }}
            type="file"
            multiple
            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
            onChange={(event) => selectFiles(event.target.files)}
          />
          <input
            ref={folderInputRef}
            style={{ display: 'none' }}
            type="file"
            multiple
            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
            webkitdirectory=""
            directory=""
            onChange={(event) => selectFiles(event.target.files)}
          />

          {!mosaic && items.length === 0 && (
            <div className="text-center pad-2" style={{ border: '1px dashed #bcc7d3', borderRadius: '10px' }}>
              <i className="bx bx-images" style={{ fontSize: '36px', color: '#6d7f91' }} aria-hidden="true" />
              <div className="text-bold">Choose overlapping aerial images</div>
              <div className="small-text inactive-text">
                JPG or PNG. Select 2–{maxImages || 300} images, up to {maxUploadSizeMb || 60} MB total.
              </div>
              <div style={styles.pickerActions}>
                <button
                  className="btn btn-secondary radius"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <i className="bx bx-images" aria-hidden="true" /> Choose Images
                </button>
                <button
                  className="btn btn-secondary radius"
                  type="button"
                  onClick={() => folderInputRef.current?.click()}
                >
                  <i className="bx bx-folder-open" aria-hidden="true" /> Choose Folder
                </button>
              </div>
            </div>
          )}

          {isActive && (
            <div className="m-bottom-2">
              <div className="text-bold">
                {mosaic.reprocessing ? 'Reprocessing image mosaic' : 'Processing image mosaic'}… {mosaic.progress || 0}%
              </div>
              <progress value={mosaic.progress || 0} max="100" style={{ width: '100%' }} />
              <div className="small-text inactive-text">
                You can leave this page. The job will continue processing.
                {mosaic.reprocessing && hasResult ? ' Your current result will stay unchanged until you choose to use the new one.' : ''}
              </div>
              {!mosaic.reprocessing && (
                <>
                  <div className="m-top-1">
                    <button
                      className="btn btn-danger radius"
                      type="button"
                      disabled={uploading}
                      onClick={cancelProcessingAndStartOver}
                    >
                      <i className="bx bx-reset" aria-hidden="true" /> {uploading ? 'Stopping…' : 'Start Over'}
                    </button>
                  </div>
                  {renderProcessingLog()}
                </>
              )}

              {mosaic.reprocessing && hasResult && (
                <>
                  <div className="text-bold m-top-1">Settings changed</div>
                  {settingChanges.length > 0 ? (
                    <div style={styles.settingsCompare}>
                      <div style={styles.settingsHeader}>
                        <span>Setting</span>
                        <span>Current</span>
                        <span>Reprocessed</span>
                      </div>
                      {settingChanges.map((change) => (
                        <div key={change.key} style={styles.settingsRow}>
                          <strong>{change.label}</strong>
                          <span>{change.previous}</span>
                          <span>{change.current}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="small-text inactive-text m-top-half">
                      No processing settings changed.
                    </div>
                  )}

                  <div style={styles.compareGrid}>
                  <div style={styles.compareCard}>
                    <div style={styles.compareLabel}>
                      <strong>Current Result</strong>
                      <span className="small-text inactive-text">In use now</span>
                    </div>
                    <img
                      src={mosaic.output_url}
                      alt="Current image mosaic"
                      style={styles.compareImage}
                      onClick={() => setLargeResultUrl(mosaic.output_url)}
                    />
                  </div>

                  <div style={styles.compareCard}>
                    <div style={styles.compareLabel}>
                      <strong>Reprocessed Result</strong>
                      <span className="small-text inactive-text">Processing</span>
                    </div>
                    <div
                      style={{
                        ...styles.compareImage,
                        cursor: 'default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        color: '#d5dbe1',
                        gap: '10px',
                      }}
                    >
                      <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '34px' }} aria-hidden="true" />
                      <div className="small-text">Building new result… {mosaic.progress || 0}%</div>
                    </div>
                  </div>
                  </div>

                  <div className="m-top-1">
                    <button
                      className="btn btn-danger radius"
                      type="button"
                      disabled={uploading}
                      onClick={cancelProcessingAndStartOver}
                    >
                      <i className="bx bx-reset" aria-hidden="true" /> {uploading ? 'Stopping…' : 'Start Over'}
                    </button>
                    {renderProcessingLog()}
                  </div>
                </>
              )}
            </div>
          )}

          {mosaic?.status === 'failed' && (
            <div className="danger radius pad-1 m-bottom-2">
              {mosaic.error_message || 'Image mosaic processing failed.'}
            </div>
          )}

          {items.length > 0 && !isActive && !hasCandidate && (
            <>
              <div className="text-bold">Select and arrange images</div>
              <div className="small-text inactive-text">
                Images default to natural filename order (1, 2, 10). Selected images are highlighted with a checkmark. Click the checkmark to include or exclude an image, or drag images using the grip handle to set a different capture order. A faded preview shows where an image will land. Invalid images are skipped automatically.
              </div>
              <button type="button" className="btn btn-secondary radius m-top-half"
                disabled={uploading} onClick={restoreFilenameOrder}>
                <i className="bx bx-sort-a-z" aria-hidden="true" /> Sort by Filename
              </button>
              {validatingItems.length > 0 && (
                <div className="small-text inactive-text m-top-half">
                  Checking {validatingItems.length} image{validatingItems.length === 1 ? '' : 's'}…
                </div>
              )}
              {invalidItems.length > 0 && (
                <div className="warning radius pad-1 m-top-1">
                  <strong>{invalidItems.length} invalid image{invalidItems.length === 1 ? '' : 's'} will be skipped.</strong>
                  <div className="small-text">These files could not be decoded as valid JPG or PNG images.</div>
                </div>
              )}

              <div style={styles.grid}>
                {previewItems.map((item, index) => {
                  const isDragPreview = draggedIndex !== null && dragOverIndex === index && items[draggedIndex]?.originalIndex === item.originalIndex;
                  const isInvalid = item.valid === false;
                  const isChecking = item.valid === null;
                  const isExcluded = excluded.has(item.originalIndex) || isInvalid;
                  const isSelected = !isExcluded && !isChecking;
                  return (
                    <div
                      key={item.originalIndex}
                      draggable={isSelected}
                      onDragStart={(event) => {
                        // Keep the original index even as the visual preview changes order.
                        setDraggedIndex(items.findIndex((original) => original.originalIndex === item.originalIndex));
                        setDragOverIndex(index);
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', String(item.originalIndex));
                      }}
                      onDragOver={(event) => {
                        if (draggedIndex === null) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                        if (dragOverIndex !== index) setDragOverIndex(index);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        moveDraggedItem(index);
                      }}
                      onDragEnd={clearImageDrag}
                      style={{
                        ...styles.card,
                        ...(isSelected ? styles.selectedCard : {}),
                        ...(isInvalid ? styles.invalidCard : {}),
                        opacity: isDragPreview ? 0.5 : (isInvalid ? 0.72 : (isSelected ? 1 : 0.42)),
                        ...(isDragPreview ? { border: '2px dashed #2688d8' } : {}),
                        cursor: isSelected ? 'grab' : 'default',
                        transition: 'opacity 120ms ease',
                      }}
                    >
                      {isSelected && (
                        <span
                          title="Drag to rearrange image order"
                          aria-label="Drag to rearrange image order"
                          style={{ position: 'absolute', right: '6px', bottom: '6px', color: '#2688d8', pointerEvents: 'none' }}
                        >
                          <i className="bx bx-grid-vertical" aria-hidden="true" />
                        </span>
                      )}
                      {!isInvalid && !isChecking && (
                        <button
                          type="button"
                          aria-label={isSelected ? `Exclude ${item.name}` : `Include ${item.name}`}
                          title={isSelected ? 'Selected — click to exclude' : 'Not selected — click to include'}
                          onClick={() => toggleExcluded(item.originalIndex)}
                          style={{
                            ...styles.selectionBadge,
                            background: isSelected ? '#2688d8' : '#fff',
                            color: isSelected ? '#fff' : '#2688d8',
                            borderColor: isSelected ? '#fff' : '#2688d8',
                          }}
                        >
                          <i className={isSelected ? 'bx bx-check' : 'bx bx-plus'} aria-hidden="true" />
                        </button>
                      )}
                      {isInvalid ? (
                        <div style={styles.invalidPreview}>
                          <i className="bx bx-error-circle" style={{ fontSize: '26px' }} aria-hidden="true" />
                          <strong>Invalid image</strong>
                          <span className="small-text">Skipped</span>
                        </div>
                      ) : isChecking ? (
                        <div style={styles.invalidPreview}>
                          <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '26px' }} aria-hidden="true" />
                          <span className="small-text">Checking image…</span>
                        </div>
                      ) : (
                        <img src={item.previewUrl} alt={item.name} style={styles.thumb} draggable={false} />
                      )}
                      <div
                        className="small-text text-bold"
                        title={item.name}
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginTop: '6px',
                        }}
                      >
                        {index + 1}. {item.name}
                      </div>
                    </div>
                  );
                })}
              </div>



              <hr />

              <div className="text-bold">Processing preset</div>
              <div style={styles.controls}>
                <label>
                  Preset
                  <select
                    value={settings.preset || 'balanced'}
                    onChange={(event) => applyPreset(event.target.value)}
                  >
                    {['balanced', 'strict_clean', 'more_coverage', 'custom'].map((preset) => (
                      <option key={preset} value={preset}>{PRESET_LABELS[preset]}</option>
                    ))}
                  </select>
                </label>
              </div>

              <button
                className="btn btn-secondary radius"
                type="button"
                onClick={() => setAdvanced((value) => !value)}
              >
                <i className={`bx ${advanced ? 'bx-chevron-down' : 'bx-chevron-right'}`} aria-hidden="true" />
                Advanced Settings
              </button>

              {advanced && (
                <div style={styles.advancedPanel}>
                  <div className="m-bottom-1">
                    <div className="text-bold">Advanced troubleshooting</div>
                    <div className="small-text inactive-text">
                      Use these controls only when the standard processing settings do not produce the expected result.
                    </div>
                  </div>

                <div className="m-top-1">
                  <div className="text-bold">Troubleshooting: suggest an image pair</div>
                  <div className="small-text inactive-text">
                    If two images visibly overlap but are not connecting, suggest that pair and reprocess. Existing automatic matches will not be forced together; only verified matches can influence placement. Results appear below.
                  </div>
                  <div style={{ ...styles.controls, alignItems: 'end' }}>
                    <label>
                      First image
                      <select
                        value={hintFrom}
                        onChange={(event) => setHintFrom(event.target.value)}
                        style={{ margin: 0, height: '38px' }}
                      >
                        <option value="">Choose image…</option>
                        {activeItems.map((item) => (
                          <option key={`hint-from-${item.originalIndex}`} value={item.originalIndex}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Second image
                      <select
                        value={hintTo}
                        onChange={(event) => setHintTo(event.target.value)}
                        style={{ margin: 0, height: '38px' }}
                      >
                        <option value="">Choose image…</option>
                        {activeItems.map((item) => (
                          <option key={`hint-to-${item.originalIndex}`} value={item.originalIndex}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      className="btn btn-secondary radius"
                      type="button"
                      disabled={hintFrom === '' || hintTo === '' || hintFrom === hintTo}
                      onClick={addOverlapHint}
                      style={{
                        margin: 0,
                        alignSelf: 'end',
                        height: '38px',
                        minHeight: '38px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i className="bx bx-link" aria-hidden="true" /> Suggest Image Pair
                    </button>
                  </div>

                  {hints.length > 0 && (
                    <div className="m-top-half" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {hints.map((pair) => {
                        const first = items.find((item) => item.originalIndex === pair[0]);
                        const second = items.find((item) => item.originalIndex === pair[1]);
                        return (
                          <button
                            key={`hint-${pair[0]}-${pair[1]}`}
                            className="btn btn-secondary radius"
                            type="button"
                            onClick={() => removeOverlapHint(pair)}
                            title="Remove overlap hint"
                            style={{ margin: 0 }}
                          >
                            <i className="bx bx-link-alt" aria-hidden="true" /> {first?.name || pair[0]} ↔ {second?.name || pair[1]} <i className="bx bx-x" aria-hidden="true" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {manualPairResults.length > 0 && (
                    <div className="m-top-1" style={{ border: '1px solid #d5dce5', borderRadius: '8px', padding: '12px' }}>
                      <div className="text-bold m-bottom-half">Suggested pair results (latest processing run)</div>
                      {manualPairResults.map((entry, index) => {
                        const from = activeItems[entry.image_a]?.name || `Image ${Number(entry.image_a) + 1}`;
                        const to = activeItems[entry.image_b]?.name || `Image ${Number(entry.image_b) + 1}`;
                        const statusText = !entry.accepted
                          ? `Not matched${entry.reason ? `: ${String(entry.reason).replace(/_/g, ' ')}` : ''}`
                          : entry.used_for_initial_placement
                            ? 'Matched and used to place an image'
                            : entry.connected_in_output
                              ? 'Matched; both images are in the same output region'
                              : 'Matched, but these images remain in separate output regions';
                        return (
                          <div key={`manual-result-${entry.image_a}-${entry.image_b}-${index}`} className="small-text m-bottom-half">
                            <strong>{from} ↔ {to}:</strong>{' '}{statusText}
                            {entry.accepted && entry.verified_matching_points !== undefined
                              ? ` (${entry.verified_matching_points} verified matching points)`
                              : ''}
                            {entry.already_tested_automatically ? ' — already checked automatically' : ' — newly tested pair'}
                          </div>
                        );
                      })}
                      <div className="small-text inactive-text">
                        Matching does not guarantee a pair was needed to form the mosaic. A hint never forces an unverified connection.
                      </div>
                    </div>
                  )}
                </div>

                  <hr />
                  <div className="text-bold m-bottom-half">Stitching controls</div>
                  <div style={styles.advancedGrid}>
                    <label style={styles.field}>
                      {fieldLabel('Maximum processing resolution', settingField('max_dimension').help, '(200–2000 px)')}
                      <input
                        style={styles.fieldControl}
                        type="number"
                        min="200"
                        max="2000"
                        step="50"
                        value={settings.max_dimension || 700}
                        onChange={(e) => updateSetting('max_dimension', Number(e.target.value))}
                        onBlur={() => clampSetting('max_dimension')}
                      />
                    </label>
                    <label style={styles.field}>
                      {fieldLabel('Match strictness', settingField('feature_match_ratio').help, '(0.45–0.95)')}
                      <input
                        style={styles.fieldControl}
                        type="number"
                        min="0.45"
                        max="0.95"
                        step="0.01"
                        value={settings.feature_match_ratio || 0.70}
                        onChange={(e) => updateSetting('feature_match_ratio', Number(e.target.value))}
                        onBlur={() => clampSetting('feature_match_ratio')}
                      />
                    </label>
                    <label style={styles.field}>
                      {fieldLabel('Minimum verified matching points', settingField('min_good_matches').help, '(4–100)')}
                      <input
                        style={styles.fieldControl}
                        type="number"
                        min="4"
                        max="100"
                        step="1"
                        value={settings.min_good_matches || 7}
                        onChange={(e) => updateSetting('min_good_matches', Number(e.target.value))}
                        onBlur={() => clampSetting('min_good_matches')}
                      />
                    </label>
                    <label style={styles.field}>
                      {fieldLabel('Match position tolerance', settingField('reprojection_threshold_px').help, '(0.5–12 px)')}
                      <input
                        style={styles.fieldControl}
                        type="number"
                        min="0.5"
                        max="12"
                        step="0.1"
                        value={settings.reprojection_threshold_px || 3}
                        onChange={(e) => updateSetting('reprojection_threshold_px', Number(e.target.value))}
                        onBlur={() => clampSetting('reprojection_threshold_px')}
                      />
                    </label>
                    <label style={styles.field}>
                      {fieldLabel('Image blending', 'Controls how overlapping image areas are combined in the final mosaic.')}
                      <select
                        style={styles.fieldControl}
                        value={settings.blend_method || 'distance_transform'}
                        onChange={(e) => updateSetting('blend_method', e.target.value)}
                      >
                        <option value="distance_transform">Distance Transform</option>
                        <option value="hard_seam">Hard Seam</option>
                      </select>
                    </label>
                    <label style={styles.field}>
                      {fieldLabel('Nearby images to check', settingField('neighbor_radius').help, '(1–8 images)')}
                      <input
                        style={styles.fieldControl}
                        type="number"
                        min="1"
                        max="8"
                        step="1"
                        value={settings.neighbor_radius || 3}
                        onChange={(e) => updateSetting('neighbor_radius', Number(e.target.value))}
                        onBlur={() => clampSetting('neighbor_radius')}
                      />
                    </label>
                    <label style={styles.field}>
                      {fieldLabel('Extra distant images to check', settingField('nonlocal_candidates_per_frame').help, '(0–20 images)')}
                      <input
                        style={styles.fieldControl}
                        type="number"
                        min="0"
                        max="20"
                        step="1"
                        value={settings.nonlocal_candidates_per_frame ?? 8}
                        onChange={(e) => updateSetting('nonlocal_candidates_per_frame', Number(e.target.value))}
                        onBlur={() => clampSetting('nonlocal_candidates_per_frame')}
                      />
                    </label>
                    <div style={styles.checkboxField}>
                      <label style={styles.checkboxToggleLabel}>
                        <input
                          type="checkbox"
                          checked={settings.require_spatial_distribution !== false}
                          onChange={(e) => updateSetting('require_spatial_distribution', e.target.checked)}
                        />
                        <span>Reject clustered matching points</span>
                      </label>
                      {helpTip('Rejects matches whose evidence is concentrated in too small an area of the image.')}
                    </div>
                  </div>
                  <hr />
                  <div className="text-bold">Camera calibration / lens correction</div>
                  <div className="small-text inactive-text m-bottom-half">
                    Optional. Import or enter the calibration values defined for this camera to undistort accepted images before feature matching and stitching.
                  </div>
                  <div style={{ ...styles.controls, alignItems: 'end' }}>
                    <label style={styles.fileField}>
                      <span>Import calibration JSON</span>
                      <input
                        style={styles.fileInput}
                        type="file"
                        accept=".json,application/json"
                        onChange={(e) => importCalibrationJson(e.target.files?.[0])}
                      />
                    </label>
                    <span className="small-text inactive-text">
                      {calibrationFileName
                        ? `Loaded ${calibrationFileName}${calibrationReprojectionError !== null ? ` — reprojection error ${calibrationReprojectionError.toFixed(4)} px` : ''}`
                        : 'Supports JSON exported by Camera Calibration Utilities. You can still edit the imported values below.'}
                    </span>
                  </div>

                  <label style={styles.checkboxField}>
                    <input
                      type="checkbox"
                      checked={Boolean(settings.camera_calibration_enabled)}
                      onChange={(e) => updateSetting('camera_calibration_enabled', e.target.checked)}
                    />
                    <span>Correct camera lens distortion before stitching</span>
                  </label>

                  {settings.camera_calibration_enabled && (
                    <>
                      <div style={styles.advancedGrid}>
                        {[
                          ['camera_fx', 'Camera fx', 'Focal length in pixels from the camera matrix.'],
                          ['camera_fy', 'Camera fy', 'Focal length in pixels from the camera matrix.'],
                          ['camera_cx', 'Camera cx', 'Principal-point x coordinate in pixels.'],
                          ['camera_cy', 'Camera cy', 'Principal-point y coordinate in pixels.'],
                          ['camera_k1', 'Distortion k1', 'Radial distortion coefficient k1.'],
                          ['camera_k2', 'Distortion k2', 'Radial distortion coefficient k2.'],
                          ...(settings.camera_model === 'fisheye'
                            ? [
                                ['camera_k3', 'Fisheye k3', 'OpenCV fisheye distortion coefficient k3.'],
                                ['camera_k4', 'Fisheye k4', 'OpenCV fisheye distortion coefficient k4.'],
                              ]
                            : [
                                ['camera_p1', 'Tangential p1', 'OpenCV standard tangential distortion coefficient p1.'],
                                ['camera_p2', 'Tangential p2', 'OpenCV standard tangential distortion coefficient p2.'],
                                ['camera_k3', 'Distortion k3', 'OpenCV standard radial distortion coefficient k3.'],
                                ...(settings.camera_k4 !== null || settings.camera_k5 !== null || settings.camera_k6 !== null
                                  ? [
                                      ['camera_k4', 'Distortion k4', 'Optional rational-model radial coefficient k4.'],
                                      ['camera_k5', 'Distortion k5', 'Optional rational-model radial coefficient k5.'],
                                      ['camera_k6', 'Distortion k6', 'Optional rational-model radial coefficient k6.'],
                                    ]
                                  : []),
                              ]),
                        ].map(([key, label, help]) => (
                          <label key={key} style={styles.field}>
                            {fieldLabel(label, help)}
                            <input
                              style={styles.fieldControl}
                              type="number"
                              step="any"
                              value={settings[key] ?? ''}
                              onChange={(e) => updateSetting(key, e.target.value === '' ? null : Number(e.target.value))}
                            />
                          </label>
                        ))}
                        <label style={styles.field}>
                          {fieldLabel('Calibration image width', 'Width of the images used when these calibration values were generated. Leave both dimensions blank if they match the uploaded images.', '(optional)')}
                          <input
                            style={styles.fieldControl}
                            type="number"
                            min="1"
                            step="1"
                            value={settings.camera_calibration_width ?? ''}
                            onChange={(e) => updateSetting('camera_calibration_width', e.target.value === '' ? null : Number(e.target.value))}
                          />
                        </label>
                        <label style={styles.field}>
                          {fieldLabel('Calibration image height', 'Height of the images used when these calibration values were generated. Leave both dimensions blank if they match the uploaded images.', '(optional)')}
                          <input
                            style={styles.fieldControl}
                            type="number"
                            min="1"
                            step="1"
                            value={settings.camera_calibration_height ?? ''}
                            onChange={(e) => updateSetting('camera_calibration_height', e.target.value === '' ? null : Number(e.target.value))}
                          />
                        </label>
                        <label style={styles.field}>
                          {fieldLabel('Lens correction balance', '0 crops more aggressively for cleaner geometry; 1 preserves more field of view. Start at 0 for measurement testing.', '(0–1)')}
                          <input
                            style={styles.fieldControl}
                            type="number"
                            min="0"
                            max="1"
                            step="0.05"
                            value={settings.camera_balance ?? 0}
                            onChange={(e) => updateSetting('camera_balance', Number(e.target.value))}
                          />
                        </label>
                      </div>
                      <div className="small-text inactive-text m-top-half">
                        These values are applied only to this processing run. For initial measurement tests, start with balance 0 and compare the result against the same batch with correction turned off.
                      </div>
                    </>
                  )}

                </div>
              )}

              {(error || settingsRangeError || cameraCalibrationError) && (
                <div className="danger radius pad-1 m-top-1 m-bottom-1">
                  {error || settingsRangeError || cameraCalibrationError}
                </div>
              )}

              <div style={styles.processingActions}>
                <button
                  className="btn btn-success radius"
                  type="button"
                  disabled={uploading || Boolean(settingsRangeError) || Boolean(cameraCalibrationError)}
                  onClick={mosaic ? reprocess : submitNew}
                >
                  <i className="bx bx-refresh" aria-hidden="true" />{' '}
                  {uploading
                    ? (uploadProgress.total > 0
                      ? `Uploading ${uploadProgress.completed}/${uploadProgress.total}…`
                      : 'Finalizing…')
                    : mosaic
                      ? 'Reprocess Mosaic'
                      : 'Process Images'}
                </button>
                <button
                  className="btn btn-danger radius"
                  type="button"
                  disabled={uploading}
                  onClick={resetMosaic}
                >
                  <i className="bx bx-reset" aria-hidden="true" /> Start Over
                </button>
                <span className="small-text inactive-text">
                  {activeItems.length} of {items.length} images enabled
                </span>
              </div>
            </>
          )}

          {hasResult && !isActive && hasCandidate && (
            <div className="m-top-2" ref={resultRef}>
              <style>{`
                .image-mosaic-keep-current { background: #737b85 !important; border: 1px solid #737b85 !important; color: #fff !important; transition: background-color .15s ease, border-color .15s ease; }
                .image-mosaic-keep-current:hover:not(:disabled) { background: #5d6671 !important; border-color: #5d6671 !important; color: #fff !important; }
                .image-mosaic-keep-current:active:not(:disabled) { background: #434c57 !important; border-color: #434c57 !important; color: #fff !important; }
                .image-mosaic-keep-current:focus-visible { outline: 3px solid #247bd1 !important; outline-offset: 2px; }
              `}</style>
              <div className="text-bold m-top-1">Settings changed</div>
              {settingChanges.length > 0 ? (
                <div style={styles.settingsCompare}>
                  <div style={styles.settingsHeader}>
                    <span>Setting</span>
                    <span>Current</span>
                    <span>Reprocessed</span>
                  </div>
                  {settingChanges.map((change) => (
                    <div key={change.key} style={styles.settingsRow}>
                      <strong>{change.label}</strong>
                      <span>{change.previous}</span>
                      <span>{change.current}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="small-text inactive-text m-top-half">
                  No processing settings changed.
                </div>
              )}
              <div className="text-bold m-bottom-half">Compare Results</div>
              <div className="small-text inactive-text">
                Choose which result to keep. You can open either image to inspect it before deciding.
              </div>
              <div style={styles.compareGrid}>
                <div style={styles.compareCard}>
                  <div style={styles.compareLabel}>
                    <strong>Current Result</strong>
                    <span className="small-text inactive-text">In use now</span>
                  </div>
                  <img
                    src={mosaic.output_url}
                    alt="Current image mosaic"
                    style={styles.compareImage}
                    onClick={() => setLargeResultUrl(mosaic.output_url)}
                  />
                  <a className="btn radius expand m-top-1 image-mosaic-download-button" href={mosaic.output_url} target="_blank" rel="noopener noreferrer" download>
                    <i className="bx bx-download" aria-hidden="true" /> Download Current
                  </a>
                  <button className="btn radius expand m-top-half image-mosaic-keep-current" type="button"
                    disabled={uploading} onClick={() => resolveCandidate('discard')}
                    style={{ marginBottom: 0, fontWeight: 700 }}>
                    <i className="bx bx-check" aria-hidden="true" /> Keep Current Result
                  </button>
                </div>

                <div style={styles.compareCard}>
                  <div style={styles.compareLabel}>
                    <strong>Reprocessed Result</strong>
                    <span className="small-text inactive-text">Candidate</span>
                  </div>
                  <img
                    src={mosaic.candidate_output_url}
                    alt="Reprocessed image mosaic"
                    style={styles.compareImage}
                    onClick={() => setLargeResultUrl(mosaic.candidate_output_url)}
                  />
                  <a className="btn radius expand m-top-1 image-mosaic-download-button" href={mosaic.candidate_output_url} target="_blank" rel="noopener noreferrer" download>
                    <i className="bx bx-download" aria-hidden="true" /> Download Reprocessed
                  </a>
                  <button className="btn btn-success radius expand m-top-half" type="button"
                    disabled={uploading} onClick={() => resolveCandidate('accept')} style={{ marginBottom: 0 }}>
                    <i className="bx bx-check" aria-hidden="true" /> Keep Reprocessed Result
                  </button>
                </div>
              </div>

              {renderProcessingLog()}

              <div style={styles.actions}>
                <button className="btn btn-secondary radius" style={styles.action} type="button" onClick={openCompareViewer}>
                  <i className="bx bx-columns" aria-hidden="true" /> Compare Larger
                </button>

              </div>
            </div>
          )}

          {hasResult && !isActive && !hasCandidate && (
            <div className="m-top-2" ref={resultRef}>
              <div className="text-bold m-bottom-half">Mosaic result</div>
              <div className="small-text inactive-text m-bottom-1">
                Click the preview to view it larger.
              </div>
              <img
                src={mosaic.output_url}
                alt="Generated image mosaic"
                style={styles.resultImage}
                onClick={() => setLargeResultUrl(mosaic.output_url)}
              />
              {mosaic.error_message && (
                <div className="warning radius pad-1 m-top-1">
                  <i className="bx bx-error-circle" aria-hidden="true" /> {mosaic.error_message}
                </div>
              )}
              {processingLog.length > 0 && renderProcessingLog()}

              <div style={styles.actions}>
                <button className="btn radius" style={styles.action} type="button" onClick={() => setShowDistanceViewer(true)}>
                  <i className="bx bx-ruler" aria-hidden="true" /> Measure Distances
                </button>
                <a className="btn btn-success radius" style={styles.action} href={mosaic.output_url} target="_blank" rel="noopener noreferrer" download>
                  <i className="bx bx-download" aria-hidden="true" /> Download Image
                </a>
                <button className="btn btn-warning radius" style={styles.action} type="button" onClick={() => setShowReplaceWarning(true)}>
                  <i className="bx bx-refresh" aria-hidden="true" /> Create Another
                </button>
              </div>

              {showReplaceWarning && (
                <div className="warning radius pad-1 m-top-1">
                  <div className="text-bold m-bottom-half">
                    <i className="bx bx-error" aria-hidden="true" /> Creating another will replace this image mosaic
                  </div>
                  <div className="small-text m-bottom-1">
                    Download the image first if you want to keep a copy of this mosaic.
                  </div>
                  <div style={styles.actions}>
                    <button className="btn btn-secondary radius" style={styles.action} type="button" onClick={() => setShowReplaceWarning(false)}>
                      Cancel
                    </button>
                    <button className="btn radius" style={styles.action} type="button" onClick={resetMosaic}>
                      Continue and Replace
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {showDistanceViewer && mosaic?.output_url && media?.id && (
            <MosaicDistanceViewer
              key={`${media.id}-${mosaic.output_url}`}
              mosaicId={media.id}
              imageUrl={mosaic.output_url}
              imageId={mosaic.measurement_image_id}
              savedMeasurements={mosaic.measurement_data}
              endpoint={endpoint}
              onClose={() => {
                setShowDistanceViewer(false);
                endpoint.get(`/client_api/v1/mixed_medias/${media.id}`)
                  .then((response) => setMosaic(response.data.image_mosaic))
                  .catch(() => {});
              }}
            />
          )}

          {showCompareViewer && mosaic?.output_url && mosaic?.candidate_output_url && (
            <div
              style={styles.compareViewerOverlay}
              role="dialog"
              aria-modal="true"
              aria-label="Compare image mosaic results"
              onKeyDown={(event) => {
                if (event.key === 'Escape') setShowCompareViewer(false);
              }}
              tabIndex={-1}
            >
              <div style={styles.compareViewerHeader}>
                <div>
                  <div className="text-bold">Compare Mosaic Results</div>
                  <div className="small-text" style={{ color: '#c9d1d9' }}>
                    Zoom and pan stay synchronized so you can inspect the same area in both results.
                  </div>
                </div>

                <div style={styles.compareViewerControls}>
                  <button
                    className={`btn radius ${compareMode === 'side_by_side' ? '' : 'btn-secondary'}`}
                    type="button"
                    onClick={() => setCompareMode('side_by_side')}
                  >
                    <i className="bx bx-columns" aria-hidden="true" /> Side by Side
                  </button>
                  <button
                    className={`btn radius ${compareMode === 'slider' ? '' : 'btn-secondary'}`}
                    type="button"
                    onClick={() => {
                      setCompareMode('slider');
                      setComparePan({ x: 0, y: 0 });
                      setCompareDragging(false);
                    }}
                  >
                    <i className="bx bx-slider-alt" aria-hidden="true" /> Slider
                  </button>
                  <button className="btn btn-secondary radius" type="button" onClick={resetCompareView}>
                    <i className="bx bx-reset" aria-hidden="true" /> Reset View
                  </button>
                  <button className="btn btn-danger radius" type="button" onClick={() => setShowCompareViewer(false)}>
                    <i className="bx bx-x" aria-hidden="true" /> Close
                  </button>
                </div>
              </div>

              {compareMode === 'slider' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', color: '#fff' }}>
                  <span className="small-text">Current</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={compareSlider}
                    onChange={(event) => setCompareSlider(Number(event.target.value))}
                    style={{ flex: 1, margin: 0 }}
                    aria-label="Comparison slider"
                  />
                  <span className="small-text">Reprocessed</span>
                </div>
              )}

              <div
                style={{
                  ...styles.compareViewport,
                  cursor: compareMode === 'side_by_side' ? (compareDragging ? 'grabbing' : 'grab') : 'default',
                }}
                onWheel={handleCompareWheel}
                onMouseDown={compareMode === 'side_by_side' ? startComparePan : undefined}
                onMouseMove={compareMode === 'side_by_side' ? moveComparePan : undefined}
                onMouseUp={compareMode === 'side_by_side' ? stopComparePan : undefined}
                onMouseLeave={compareMode === 'side_by_side' ? stopComparePan : undefined}
              >
                {compareMode === 'side_by_side' ? (
                  <div style={styles.compareViewportGrid}>
                    <div style={styles.comparePane}>
                      <div style={styles.comparePaneLabel}>Current Result</div>
                      <img
                        src={mosaic.output_url}
                        alt="Current image mosaic comparison"
                        draggable={false}
                        style={{
                          ...styles.compareViewerImage,
                          transform: `translate(${comparePan.x}px, ${comparePan.y}px) scale(${compareZoom})`,
                        }}
                      />
                    </div>
                    <div style={styles.comparePane}>
                      <div style={styles.comparePaneLabel}>Reprocessed Result</div>
                      <img
                        src={mosaic.candidate_output_url}
                        alt="Reprocessed image mosaic comparison"
                        draggable={false}
                        style={{
                          ...styles.compareViewerImage,
                          transform: `translate(${comparePan.x}px, ${comparePan.y}px) scale(${compareZoom})`,
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                    <div style={{ ...styles.comparePaneLabel, right: 'auto' }}>Current Result</div>
                    <img
                      src={mosaic.output_url}
                      alt="Current image mosaic comparison"
                      draggable={false}
                      style={{
                        ...styles.compareViewerImage,
                        transform: `scale(${compareZoom})`,
                      }}
                    />
                    <img
                      src={mosaic.candidate_output_url}
                      alt="Reprocessed image mosaic comparison"
                      draggable={false}
                      style={{
                        ...styles.compareViewerImage,
                        clipPath: `inset(0 0 0 ${compareSlider}%)`,
                        transform: `scale(${compareZoom})`,
                      }}
                    />
                    <div style={{ ...styles.sliderDivider, left: `${compareSlider}%` }} />
                    <div style={{ ...styles.comparePaneLabel, left: 'auto', right: '12px' }}>Reprocessed Result</div>
                  </div>
                )}
              </div>

              <div style={styles.compareHelp}>
                {compareMode === 'side_by_side'
                  ? 'Scroll to zoom • Drag to pan • Esc closes the viewer'
                  : 'Scroll to zoom • Use the slider to wipe between results • Esc closes the viewer'}
              </div>
            </div>
          )}

          {largeResultUrl && (
            <div
              style={styles.overlay}
              role="button"
              tabIndex={0}
              aria-label="Close enlarged mosaic"
              onClick={() => setLargeResultUrl(null)}
              onKeyDown={(event) => {
                if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
                  setLargeResultUrl(null);
                }
              }}
            >
              <img src={largeResultUrl} alt="Image mosaic enlarged" style={styles.overlayImage} />
            </div>
          )}
        </div>
      </div>
      </ElementContainer>
    </>
  );
};

export default ImageMosaicArtifact;
